import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, AlertCircle, Activity, FileText, Scan, X, ImageIcon, Database, Cpu, Search, CheckCircle, Loader2 } from 'lucide-react';

interface AnalysisStep {
  id: string;
  title: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  progress: number;
}

interface DetectedAsset {
  id: string;
  name: string;
  type: string;
  confidence: number;
}

export const HMIAnalysis: React.FC = () => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detectedAssets, setDetectedAssets] = useState<DetectedAsset[]>([]);

  const [steps, setSteps] = useState<AnalysisStep[]>([
    {
      id: 'preprocessing',
      title: 'Image Preprocessing',
      status: 'pending',
      progress: 0
    },
    {
      id: 'detection',
      title: 'Component Detection',
      status: 'pending',
      progress: 0
    },
    {
      id: 'recognition',
      title: 'Pattern Recognition',
      status: 'pending',
      progress: 0
    },
    {
      id: 'classification',
      title: 'Asset Classification',
      status: 'pending',
      progress: 0
    },
    {
      id: 'validation',
      title: 'Result Validation',
      status: 'pending',
      progress: 0
    }
  ]);

  const updateStepProgress = async (stepId: string, targetProgress: number) => {
    const increment = 5;
    const interval = 50;
    let currentProgress = 0;

    while (currentProgress < targetProgress) {
      await new Promise(resolve => setTimeout(resolve, interval));
      currentProgress = Math.min(currentProgress + increment, targetProgress);
      
      setSteps(prev => prev.map(s =>
        s.id === stepId ? { 
          ...s, 
          progress: currentProgress,
          status: currentProgress === 100 ? 'completed' : 'processing'
        } : s
      ));
    }
  };

  const simulateAnalysis = async () => {
    setIsAnalyzing(true);
    
    // Step 1: Preprocessing
    await updateStepProgress('preprocessing', 100);

    // Step 2: Component Detection
    setSteps(prev => prev.map(step => 
      step.id === 'detection' ? { ...step, status: 'processing' } : step
    ));

    const mockAssets: DetectedAsset[] = [
      { id: 'pump-101', name: 'Centrifugal Pump', type: 'pump', confidence: 0.98 },
      { id: 'valve-201', name: 'Control Valve', type: 'valve', confidence: 0.95 },
      { id: 'sensor-301', name: 'Pressure Sensor', type: 'sensor', confidence: 0.92 },
      { id: 'tank-401', name: 'Storage Tank', type: 'tank', confidence: 0.97 },
      { id: 'meter-501', name: 'Flow Meter', type: 'meter', confidence: 0.94 },
      { id: 'motor-601', name: 'Electric Motor', type: 'motor', confidence: 0.91 }
    ];

    for (let i = 0; i < mockAssets.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 800));
      setDetectedAssets(prev => [...prev, mockAssets[i]]);
      setSteps(prev => prev.map(step =>
        step.id === 'detection' ? { 
          ...step, 
          progress: ((i + 1) / mockAssets.length) * 100 
        } : step
      ));
    }

    // Step 3: Pattern Recognition
    await updateStepProgress('recognition', 100);

    // Step 4: Asset Classification
    await updateStepProgress('classification', 100);

    // Step 5: Validation
    await updateStepProgress('validation', 100);

    // Complete analysis
    setIsAnalyzing(false);

    // Navigate to data management section after completion
    setTimeout(() => {
      const event = new CustomEvent('changeSection', { detail: 'data-management' });
      window.dispatchEvent(event);
    }, 1000);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxSize: 20 * 1024 * 1024,
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setUploadedImage(URL.createObjectURL(file));
      simulateAnalysis();
    }
  });

  const getStepIcon = (status: AnalysisStep['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'processing':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-gray-300" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Scan className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-bold text-gray-900">HMI Analysis</h2>
          </div>
        </div>

        {!uploadedImage && (
          <div
            {...getRootProps()}
            className={`h-64 flex flex-col items-center justify-center cursor-pointer transition-colors rounded-lg border-2 border-dashed ${
              isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-500'
            }`}
          >
            <input {...getInputProps()} />
            <div className="text-center">
              {isDragActive ? (
                <>
                  <Upload className="h-16 w-16 text-blue-500 mx-auto mb-4" />
                  <p className="text-lg font-medium text-blue-600">Drop the HMI diagram here</p>
                </>
              ) : (
                <>
                  <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">Upload HMI Diagram</p>
                  <p className="text-sm text-gray-500 mb-4">Drag and drop or click to select</p>
                  <p className="text-xs text-gray-400">Supported formats: JPEG, PNG (max 20MB)</p>
                </>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600">{error}</p>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 text-red-500 hover:text-red-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {uploadedImage && (
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Original Image */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Original HMI Diagram</h3>
                <div className="relative aspect-video bg-gray-50 rounded-lg overflow-hidden">
                  <img
                    src={uploadedImage}
                    alt="Original HMI Diagram"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Analysis Progress */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Analysis Progress</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-4">
                    {steps.map((step) => (
                      <div key={step.id} className="flex items-center space-x-3">
                        <div className="flex-shrink-0">{getStepIcon(step.status)}</div>
                        <div className="flex-grow min-w-0">
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-sm font-medium text-gray-900">{step.title}</p>
                            {step.progress > 0 && (
                              <span className="text-xs text-gray-500">{Math.round(step.progress)}%</span>
                            )}
                          </div>
                          <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-500 rounded-full ${
                                step.status === 'completed' 
                                  ? 'bg-green-500' 
                                  : step.status === 'error'
                                  ? 'bg-red-500'
                                  : 'bg-blue-500'
                              }`}
                              style={{ width: `${step.progress}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Detected Assets */}
            {detectedAssets.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Detected Assets</h3>
                <div className="grid grid-cols-2 gap-4">
                  {detectedAssets.map((asset) => (
                    <div
                      key={asset.id}
                      className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg animate-fade-in"
                    >
                      <Database className="h-5 w-5 text-blue-500" />
                      <div className="flex-grow">
                        <p className="text-sm font-medium text-gray-900">{asset.name}</p>
                        <p className="text-xs text-gray-500">Type: {asset.type}</p>
                      </div>
                      <span className="text-xs font-medium text-gray-500">
                        {(asset.confidence * 100).toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes fade-in {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-fade-in {
            animation: fade-in 0.5s ease-out forwards;
          }
        `}
      </style>
    </div>
  );
};